import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { FormulaOperationMode } from 'src/app/controller/manage-tracker/enums/formula-operation-mode';
import { CommonStatusType } from 'src/app/globals/custom-types/global-custom-types';
import { CommonStatus, CommonStatusDetails } from 'src/app/globals/globals';
import { ChallengesService } from 'src/app/model/challenges/challenges.service';
import { TextDateSearchModel } from 'src/app/shared/modules/search/models/text-date-search-model';
import SystemConstants from 'src/app/util/constants/SystemConstants';
import { LocalizationService, TranslationParams } from 'src/app/util/service/internationalization/localization.service';
import { Translations } from 'src/app/util/service/internationalization/translations.service';
import { MapTypeDetails } from '../../constants/map-type-details';
import { MapMode } from '../../enum/map-mode';
import { MapType } from '../../enum/map-type';
import { AutoMap, CustomMapQuery, ICustomMap, ManualMap } from '../../models/i-custom-map';
import { MapMarkerInfo } from '../../models/map-marker-info';
import { MapFormComponent } from '../map-form/map-form.component';

@Component({
    selector: 'app-choose-map-pop-up',
    templateUrl: './choose-map-pop-up.component.html',
    styleUrls: ['./choose-map-pop-up.component.scss'],
    standalone: false
})
export class ChooseMapPopUpComponent implements OnInit {
  @ViewChildren("mapForm") mapFormRefs: QueryList<MapFormComponent>

  @Input() chosenMap?: Partial<ICustomMap>;
  @Input() mode?: FormulaOperationMode;
  @Input() token: string;
  @Input() selectedLocaleId: string[];
  @Output() popUpClosed : EventEmitter<string> = new EventEmitter<string>();
  @Output() mapChosen: EventEmitter<Partial<ICustomMap>> = new EventEmitter<Partial<ICustomMap>>();

  chosenIndex: number;
  mapList: ICustomMap[];
  numberOfMapsInList: number = SystemConstants.DEFAULT_LIMIT_OF_PAGE_ITEMS;
  searchDetails: TextDateSearchModel;
  totalMapCount: number;

  constructor(public translations: Translations,
    private localizationService: LocalizationService,
    private challengeService: ChallengesService,
    private toastr: ToastrService) { }

  ngOnInit() {
    this.initializeDefaults();
  }

  chooseMap(map: Partial<ICustomMap>): void {
    this.mapChosen.emit(map);
  }

  closePopUp(): void {
    this.popUpClosed.emit();
  }

  doesChosenMapLieInList(): boolean {
    if(this.isChooseModeEnabled && this.chosenMap && this.mapList && this.mapList.length > 0) {
      let mapIndex = this.mapList.findIndex((map)=> map._id===this.chosenMap._id);
      return (mapIndex || mapIndex === 0) && mapIndex > -1
    }
  }

  editMap(mapIndex: number): void {
    this.setModeAsEditMode(mapIndex);
  }

  getEndLocationOfMap(map: Partial<ICustomMap>): string {
    if(this.isMapAutomatic(map.mapType))
      return (map as AutoMap).endLocation
  }

  getStartLocationOfMap(map: Partial<ICustomMap>): string {
    if(this.isMapManual(map.mapType))
      return (map as ManualMap).formattedAddress
    else if(this.isMapAutomatic(map.mapType))
      return (map as AutoMap).startLocation
  }

  getStatusLabelFromStatusNumber(status: CommonStatusType): string {
    return CommonStatusDetails[status].label
  }

  getTranslations(key: string, options?: TranslationParams): string {
    return this.localizationService.getTranslation(key, options)
  }

  initializeDefaults(): void {
    this.setModeAsInfoMode();
    this.setMapListAndCounts();
    this.searchDetails = new TextDateSearchModel();
  }

  isMapAutomatic(mapType: MapType): boolean {
    return mapType === MapType.AUTO
  }

  isMapManual(mapType: MapType): boolean {
    return mapType === MapType.MANUAL
  }

  loadMoreMaps(): void {
    this.numberOfMapsInList = this.numberOfMapsInList + SystemConstants.DEFAULT_LIMIT_OF_PAGE_ITEMS;
    this.setMapListAndCounts();
  }

  searchByText(text: string): void {
    this.searchDetails.text = text;
    this.numberOfMapsInList = SystemConstants.DEFAULT_SKIP_OF_PAGE_ITEMS;
    this.setMapListAndCounts();
  }

  setChooseModeForSelectedMap(mapId: string) {
    let mapIndex = this.mapList.findIndex((map)=> map._id===mapId);
    if((mapIndex || mapIndex ===0) && mapIndex > -1)
      this.setModeAsChooseMode(mapIndex);
  }

  setMapCounts(): void {
    const query: CustomMapQuery = {limit: this.numberOfMapsInList, status : CommonStatus.ACTIVE};
    this.setLocaleQuery(query);
    this.setSearchQuery(query);
    this.challengeService.getChallengMapCounts(query).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.totalMapCount = res.data ? res.data : 0;
      }
    }, err => {
      if (err && err.error && err.error.error && err.error.error.message) {
        this.toastr.error(SystemConstants.ERROR, err.error.error.message);
      }
    });
  }

  setMapList(): void {
    const query: CustomMapQuery = {limit: this.numberOfMapsInList, status : CommonStatus.ACTIVE};
    this.setLocaleQuery(query);
    this.setSearchQuery(query);
    this.challengeService.getChallengMapList(query).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.mapList = res.data;
        if(this.chosenMap && this.chosenMap._id && !this.isChooseModeEnabled) {
          this.setChooseModeForSelectedMap(this.chosenMap._id);
        }
      } else {
        this.toastr.error(SystemConstants.ERROR, res.errMsg);
      }
    }, err => {
      if (err && err.error && err.error.error && err.error.error.message) {
        this.toastr.error(SystemConstants.ERROR, err.error.error.message);
      }
    });
  }

  setMapListAndCounts(): void {
    forkJoin([
      this.setMapList(),
      this.setMapCounts()
    ])
  }

  setModeAsAddMode(): void {
    if(this.isEditModeEnabled)
      this.mapFormRefs.first.resetForm();
    this.mode = FormulaOperationMode.ADD_MODE;
    this.chosenIndex = -1;
  }

  setModeAsChooseMode(index: number): void {
    this.mode = FormulaOperationMode.CHOOSE_MODE;
    this.chosenIndex = index;
    this.chosenMap = {...this.mapList[this.chosenIndex]};
  }

  setModeAsEditMode(index: number): void {
    if(index > -1) {
      this.mode = FormulaOperationMode.EDIT_MODE;
      this.chosenIndex = index;
    }
  }

  setModeAsInfoMode(): void {
    this.mode = FormulaOperationMode.POP_UP_INFO_MODE;
    this.chosenIndex = -1;
  }

  setSearchQuery(query: CustomMapQuery): void {
    if(this.searchDetails && this.searchDetails.text)
      query.name = this.searchDetails.text
  }

  setLocaleQuery(query: CustomMapQuery): void{
    if(this.selectedLocaleId && this.selectedLocaleId[0] !== SystemConstants.ALL_LOCALE){
      query.localeId = this.selectedLocaleId
    }
  }

  showErrorMessageForMapCreation(): void {
    this.localizationService.displayToastrError(this.toastr, this.translations.GLOBAL.ERROR_MESSAGES.UNABLE_TO_CREATE)
  }

  showErrorMessageForMapUpdation(): void {
    this.localizationService.displayToastrError(this.toastr, this.translations.GLOBAL.ERROR_MESSAGES.UNABLE_TO_UPDATING)
  }

  viewMapDetails(mapIndex: number): void {
    this.chosenIndex = mapIndex;
    this.setModeAsChooseMode(mapIndex);
  }

  get isAddModeEnabled(): boolean {
    return this.mode === FormulaOperationMode.ADD_MODE;
  }

  get isChooseModeEnabled(): boolean {
    return this.mode === FormulaOperationMode.CHOOSE_MODE;
  }

  get isEditModeEnabled(): boolean {
    return this.chosenIndex > -1 && this.mode === FormulaOperationMode.EDIT_MODE;
  }

  get isInfoModeEnabled(): boolean {
    return this.mode === FormulaOperationMode.POP_UP_INFO_MODE;
  }

  get mapMode(): typeof MapMode {
    return MapMode
  }


  get mapTypeDetails(): typeof MapTypeDetails {
    return MapTypeDetails
  }

  get selectedMapDestination(): google.maps.LatLngLiteral {
    if(this.chosenMap && this.chosenMap.mapType === MapType.AUTO) {
      return (this.chosenMap as AutoMap).destination
    }
  }

  get chosenMapMarkers(): MapMarkerInfo[] {
    if(this.chosenMap && this.chosenMap.mapType === MapType.MANUAL) {
      return (this.chosenMap as ManualMap).markers
    }
  }
}
